import type { ReactNode } from 'react';

import { BootSplash } from './BootSplash';
import { BrandLockup } from './BrandLockup';
import { CrtCanvas } from './CrtCanvas';
import { HudCorner, StatusFooter } from './HudCorner';
import { EyebrowMono, GlitchText } from './IntroText';
import './login-intro.css';

const HEADLINE = 'Una consola. Todo el sistema.';

type LoginStageProps = {
  isMobile: boolean;
  /** Fase de la secuencia de entrada (0 splash … 3 tarjeta visible). */
  phase: number;
  zoom: number;
  /** La tarjeta del login; el escenario solo decide dónde y cuándo se ve. */
  tarjeta: (opts: { visible: boolean; isMobile?: boolean }) => ReactNode;
  /** Los overlays (MFA, oferta de entrada rápida) van dentro del <main>. */
  children?: ReactNode;
};

/**
 * El escenario visual del login: lienzo CRT, esquinas de HUD, titular con
 * glitch, tarjeta y pie. Extraído de LoginPage.tsx sin cambios de contenido.
 */
export function LoginStage({ isMobile, phase, zoom, tarjeta, children }: LoginStageProps) {
  const headlineFontSize = isMobile ? 30 : 56;
  const eyebrowText = isMobile
    ? '// LOGIN · v2.4'
    : '// LOGIN · MISSION CONTROL';

  return (
    <main
      className="dd-login-shell"
      style={{
        position: 'relative',
        minHeight: '100vh',
        background: '#070b0e',
        overflow: 'hidden',
        isolation: 'isolate',
      }}
    >
      {/* CRT glitch backdrop · siempre visible, modo glitch a intensidad 0.5 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: isMobile ? 0.55 : 1,
        }}
      >
        <CrtCanvas intensity={0.5} zoom={zoom} />
      </div>

      {/* Stage radial bajo el modal · pure CSS para que no interactúe con el pulse del canvas */}
      {!isMobile && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '56%',
            transform: 'translate(-50%, -50%)',
            width: 980,
            height: 720,
            maxWidth: '95vw',
            background:
              'radial-gradient(ellipse 50% 55% at center, rgba(7,11,14,0.92) 0%, rgba(7,11,14,0.7) 38%, rgba(7,11,14,0) 75%)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}

      {/* HUD corners · solo desktop */}
      {!isMobile && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 2,
            opacity: phase >= 2 ? 1 : 0,
            transition: 'opacity .5s ease',
          }}
        >
          <HudCorner pos="tl" label="SECTOR · CORE" sub="A1-9F" />
          <HudCorner pos="tr" label="UPLINK · SECURE" sub="TLS 1.3" />
          <HudCorner pos="bl" label="OBS · ACTIVE" sub="STREAM 01" />
          <HudCorner pos="br" label="CLEARANCE · AAL2" sub="ENROLLED" />
        </div>
      )}

      {/* Brand top · desktop centrado, mobile barra superior */}
      {isMobile ? (
        <div
          style={{
            position: 'absolute',
            top: 28,
            left: 22,
            right: 22,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 3,
          }}
        >
          <BrandLockup size="sm" />
          <span
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 9,
              letterSpacing: '0.16em',
              color: 'rgba(102, 224, 255, 0.7)',
              textTransform: 'uppercase',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#3ddc97',
                boxShadow: '0 0 6px #3ddc97',
              }}
            />
            online
          </span>
        </div>
      ) : (
        // Foreground desktop en FLUJO centrado (no posiciones absolutas
        // independientes) → brand + headline + card se apilan y centran, así no
        // se superponen en ningún aspect ratio ni zoom (fix UI-08 ultrawide 21:9).
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'clamp(24px, 4vh, 48px)',
            padding: 'clamp(72px, 12vh, 120px) 24px',
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              opacity: phase >= 2 ? 1 : 0,
              transform: phase >= 2 ? 'translateY(0)' : 'translateY(-6px)',
              transition: 'opacity .55s ease, transform .55s ease',
            }}
          >
            <BrandLockup size="lg" />
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 18,
              textAlign: 'center',
              maxWidth: 1000,
            }}
          >
            <div
              style={{
                opacity: phase >= 1 ? 1 : 0,
                transform: phase >= 1 ? 'translateY(0)' : 'translateY(10px)',
                transition: 'opacity .5s ease, transform .5s ease',
              }}
            >
              <EyebrowMono text={eyebrowText} delay={1100} />
            </div>
            {phase >= 1 && (
              <GlitchText
                text={HEADLINE}
                fontSize={headlineFontSize}
                weight={700}
                delay={1350}
              />
            )}
            <div
              style={{
                width: 64,
                height: 2,
                background:
                  'linear-gradient(90deg, transparent, #66e0ff, transparent)',
                opacity: phase >= 2 ? 0.8 : 0,
                transition: 'opacity .5s ease .2s',
              }}
            />
          </div>

          <div style={{ width: 'min(380px, calc(100vw - 32px))' }}>
            {tarjeta({ visible: phase >= 3 })}
          </div>
        </div>
      )}

      {/* Mobile center stack: eyebrow + headline + form */}
      {isMobile && (
        <div
          style={{
            position: 'absolute',
            top: 88,
            bottom: 60,
            left: 22,
            right: 22,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 22,
            zIndex: 3,
          }}
        >
          <div
            style={{
              opacity: phase >= 1 ? 1 : 0,
              transform: phase >= 1 ? 'translateY(0)' : 'translateY(14px)',
              transition: 'opacity .4s ease, transform .4s ease',
            }}
          >
            <EyebrowMono text={eyebrowText} delay={800} />
          </div>
          {phase >= 1 && (
            <GlitchText
              text={HEADLINE}
              fontSize={headlineFontSize}
              weight={700}
              delay={1000}
            />
          )}
          {tarjeta({ visible: phase >= 3, isMobile: true })}
        </div>
      )}

      {/* Status footer */}
      {!isMobile && phase >= 2 && (
        <div style={{ opacity: 1, transition: 'opacity .5s ease' }}>
          <StatusFooter />
        </div>
      )}
      {isMobile && phase >= 2 && (
        <div
          style={{
            position: 'absolute',
            bottom: 16,
            left: 22,
            right: 22,
            display: 'flex',
            justifyContent: 'space-between',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 8.5,
            letterSpacing: '0.14em',
            color: 'rgba(255,255,255,0.35)',
            textTransform: 'uppercase',
            zIndex: 3,
          }}
        >
          <span>build 2.4.0</span>
          <span style={{ color: '#66e0ff' }}>sa-east-1</span>
          <span>99.94%</span>
        </div>
      )}

      {phase < 1 && <BootSplash compact={isMobile} />}

      {children}
    </main>
  );
}
